import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useUsers, useCompanies, useDeactivateUser, type UserListFilter } from '@/lib/adminApi';
import type { UserAdminSummary, RoleSchemaType } from '@ventin/shared';
import { UserFormDialog } from './UserFormDialog';
import { ReassignUserDialog } from './ReassignUserDialog';

/**
 * UsersPage — /admin/users (USR-01..04).
 *
 * Layout: filter bar (role, company, New User) → paginated users table → Load more.
 * Hooks wired exclusively through adminApi (Plan 02-08); no direct apiFetch.
 *
 * Role/company invariant (Plan 02-05): CLIENT users require a companyId; ADMIN
 * and SUPPORT users must have companyId = null. UserFormDialog enforces this
 * client-side by showing the Company picker only when role === 'CLIENT'.
 * ReassignUserDialog only applies to CLIENT users — for ADMIN/SUPPORT the
 * Reassign action is disabled with tooltip "Only CLIENT users can be reassigned".
 */
export default function UsersPage() {
  const [roleFilter, setRoleFilter] = useState<RoleSchemaType | ''>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const usersFilter: UserListFilter = useMemo(() => {
    const f: UserListFilter = {};
    if (roleFilter) f.role = roleFilter;
    if (companyFilter) f.companyId = companyFilter;
    if (cursor) f.cursor = cursor;
    return f;
  }, [roleFilter, companyFilter, cursor]);

  const { data, isLoading, isError } = useUsers(usersFilter);
  const { data: companiesData } = useCompanies();
  const deactivateMut = useDeactivateUser();

  const users = useMemo(() => data?.users ?? [], [data]);
  const companies = useMemo(() => companiesData?.companies ?? [], [companiesData]);
  const companyNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companies) m.set(c.id, c.name);
    return m;
  }, [companies]);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<UserAdminSummary | undefined>(undefined);
  const [reassignTarget, setReassignTarget] = useState<UserAdminSummary | null>(null);

  async function handleDeactivate(user: UserAdminSummary) {
    try {
      await deactivateMut.mutateAsync({ id: user.id });
      toast.success('User deactivated');
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  function resetFilters() {
    setCursor(undefined);
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold leading-[1.3]">Users</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create, edit, and reassign platform users across tenants.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(undefined);
            setFormMode('create');
          }}
        >
          New User
        </Button>
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <Label htmlFor="role-filter">Filter by role</Label>
          <select
            id="role-filter"
            className="flex h-9 min-w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as RoleSchemaType | '');
              resetFilters();
            }}
          >
            <option value="">All roles</option>
            <option value="CLIENT">CLIENT</option>
            <option value="SUPPORT">SUPPORT</option>
            <option value="ADMIN">ADMIN</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="company-filter">Filter by company</Label>
          <select
            id="company-filter"
            className="flex h-9 min-w-52 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={companyFilter}
            onChange={(e) => {
              setCompanyFilter(e.target.value);
              resetFilters();
            }}
          >
            <option value="">All companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading users…</p>}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load users.</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Last login</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No users match these filters.
                  </td>
                </tr>
              )}
              {users.map((u) => {
                const isClient = u.role === 'CLIENT';
                const companyName = u.companyId ? (companyNameById.get(u.companyId) ?? '—') : '—';
                return (
                  <tr key={u.id} className="border-t">
                    <td className="px-4 py-3 font-medium">{u.fullName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{companyName}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(u);
                            setFormMode('edit');
                          }}
                          aria-label={`Edit ${u.fullName}`}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReassignTarget(u)}
                          disabled={!isClient}
                          title={
                            isClient
                              ? `Reassign ${u.fullName}`
                              : 'Only CLIENT users can be reassigned'
                          }
                          aria-label={`Reassign ${u.fullName}`}
                        >
                          Reassign
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeactivate(u)}
                          disabled={deactivateMut.isPending || !u.isActive}
                          aria-label={`Deactivate ${u.fullName}`}
                        >
                          Deactivate
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.nextCursor && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" onClick={() => setCursor(data.nextCursor ?? undefined)}>
            Load more
          </Button>
        </div>
      )}

      <UserFormDialog
        mode={formMode ?? 'create'}
        initial={editing}
        open={formMode !== null}
        onOpenChange={(o) => {
          if (!o) setFormMode(null);
        }}
      />

      <ReassignUserDialog
        user={reassignTarget}
        open={reassignTarget !== null}
        onOpenChange={(o) => {
          if (!o) setReassignTarget(null);
        }}
      />
    </div>
  );
}

function RoleBadge({ role }: { role: RoleSchemaType }) {
  const tone =
    role === 'ADMIN'
      ? 'bg-[#FEF3C7] text-[#92400E]'
      : role === 'SUPPORT'
        ? 'bg-[#DBEAFE] text-[#1E40AF]'
        : 'bg-[#ECFDF5] text-[#16A34A]';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        tone,
      )}
    >
      {role}
    </span>
  );
}
